import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LightModeIcon from '@mui/icons-material/LightMode';
import List from '@mui/material/List';
import ListIcon from '@mui/icons-material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';

const drawerWidth = 240;

/**
 * GenericNavigation
 * @param navItems Array of [label, id] for main menu items
 * @param servicesOptions Array of dropdown options for "Services" (optional)
 * @param mode Current theme mode ("dark" or "light")
 * @param modeChange Function to toggle theme mode
 * @param onNavItemClick Optional callback for nav item click (default: scroll to section)
 * @param onServiceClick Optional callback for service dropdown click (default: alert)
 */
interface GenericNavigationProps {
  navItems?: [string, string][];
  servicesOptions?: string[];
  mode?: 'light' | 'dark';
  modeChange?: () => void;
  onNavItemClick?: (id: string) => void;
  onServiceClick?: (option: string) => void;
  appBarProps?: Record<string, any>;
  toolbarProps?: Record<string, any>;
  sectionIdMap?: Record<string, string>;
}

function GenericNavigation({
  navItems = [],
  servicesOptions = [],
  mode = 'light',
  modeChange = () => { },
  onNavItemClick,
  onServiceClick,
  appBarProps = {},
  toolbarProps = {},
  sectionIdMap = {},
}: GenericNavigationProps) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navigation");
      if (navbar) {
        setScrolled(window.scrollY > navbar.clientHeight);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const defaultNavItemClick = (id: string) => {
    const targetId = sectionIdMap[id] || id;
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const drawer = (
    <Box className="navigation-bar-responsive" onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <p className="mobile-menu-top"><ListIcon />Menu</p>
      <Divider />
      <List>
        {navItems.map(([label, id]) => (
          <ListItem key={label} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} onClick={() => (onNavItemClick ? onNavItemClick(id) : defaultNavItemClick(id))}>
              <ListItemText primary={label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Dropdown logic for Services
  const handleServicesMouseEnter = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleServicesMouseLeave = () => setAnchorEl(null);
  const defaultServiceClick = (option: string) => {
    if (option === 'iOS app development') navigate('/ios');
    else alert(`Selected: ${option}`);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" id="navigation" className={`navbar-fixed-top${scrolled ? ' scrolled' : ''}`} sx={{ background: '#0d1116 !important', boxShadow: 'none' }} {...appBarProps}>
        <Toolbar className='navigation-bar' {...toolbarProps}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {mode === 'dark' ? (
            <LightModeIcon onClick={modeChange} />
          ) : (
            <DarkModeIcon onClick={modeChange} />
          )}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {navItems.map(([label, id]) => {
              if (label === "Services" && servicesOptions.length > 0) {
                return (
                  <span
                    key={label}
                    onMouseEnter={handleServicesMouseEnter}
                    style={{ display: 'inline-block' }}
                  >
                    <Button
                      sx={{ color: '#fff' }}
                      aria-controls={open ? 'services-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? 'true' : undefined}
                    >
                      Services
                    </Button>
                    <Menu
                      id="services-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleServicesMouseLeave}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                      MenuListProps={{
                        onMouseLeave: handleServicesMouseLeave,
                        style: { maxHeight: '300px', overflowY: 'auto' }
                      }}
                      PaperProps={{
                        style: { pointerEvents: 'auto' }
                      }}
                    >
                      {servicesOptions.map((option) => (
                        <MenuItem key={option} onClick={() => (onServiceClick ? onServiceClick(option) : defaultServiceClick(option))}>{option}</MenuItem>
                      ))}
                    </Menu>
                  </span>
                );
              } else {
                return (
                  <Button key={label} onClick={() => {
                    if (label === "Home") {
                      navigate('/')
                    } else {
                      (onNavItemClick ? onNavItemClick(id) : defaultNavItemClick(id))
                    }
                  }
                  } sx={{ color: '#fff' }}>
                    {label}
                  </Button>
                );
              }
            })}
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}

export default GenericNavigation;
